(() => {
  document.documentElement.classList.add('nt-js');
  const STORAGE_KEY = 'natondi.currency';
  const RATE_CACHE_KEY = 'natondi.rates';
  const RATE_REFRESH_AGE = 12 * 60 * 60 * 1000;
  const HOUR_IN_MS = 60 * 60 * 1000;
  const SUPPORTED = ['ZAR', 'EUR', 'USD'];

  const parseConfig = () => {
    const element = document.getElementById('NatondiRuntimeConfig');
    if (!element) return {};
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('[Natondi] Invalid runtime configuration.', error);
      return {};
    }
  };

  const config = parseConfig();
  const locale = config.locale === 'fr' ? 'fr-FR' : 'en-ZA';
  const fxConfig = config.fx || {};
  const rateMode = fxConfig.mode === 'manual' ? 'manual' : 'automatic';
  const maxCacheAge = Math.max(24, Number(fxConfig.maxStaleHours) || 72) * HOUR_IN_MS;
  const maxProviderRateAge = Math.max(maxCacheAge, 7 * 24 * HOUR_IN_MS);
  const rates = { ZAR: 1 };
  let rateState = rateMode === 'manual' ? 'manual' : 'loading';
  let rateTimestamp = null;

  const getSavedCurrency = () => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      return SUPPORTED.includes(saved) ? saved : 'ZAR';
    } catch (_error) {
      return 'ZAR';
    }
  };

  let activeCurrency = getSavedCurrency();

  const rateIsAvailable = (currency) => currency === 'ZAR' || Number(rates[currency]) > 0;

  const displayedCurrency = () => rateIsAvailable(activeCurrency) ? activeCurrency : 'ZAR';

  const formatMoney = (zarCents, currency) => {
    const amount = (Number(zarCents) / 100) * rates[currency];
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'ZAR' ? 0 : 2,
    }).format(amount);
  };

  const formatRateDate = (timestamp) => {
    if (!timestamp || !Number.isFinite(Number(timestamp))) return '';
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Number(timestamp)));
  };

  const refreshRateStatus = () => {
    const labels = fxConfig.labels || {};
    let message = labels.loading || 'Loading current rates…';
    if (rateState === 'unavailable') message = fxConfig.unavailableMessage || labels.unavailable || 'Currency conversion is temporarily unavailable. ZAR prices are shown.';
    if (rateState === 'manual') message = labels.manual || 'Manual exchange rates are active.';
    if (rateState === 'live' || rateState === 'cached') message = labels.updated || 'Rates updated';
    if (rateState === 'cached-fallback') message = labels.saved || 'Using the latest saved rates from';

    const showDate = fxConfig.showUpdated !== false && rateTimestamp && !['loading', 'unavailable'].includes(rateState);
    const formattedDate = showDate ? formatRateDate(rateTimestamp) : '';
    document.querySelectorAll('[data-nt-fx-status]').forEach((element) => {
      element.dataset.state = rateState;
      const messageElement = element.querySelector('[data-nt-fx-status-message]');
      if (messageElement) messageElement.textContent = formattedDate ? `${message} ${formattedDate}` : message;
    });

    document.querySelectorAll('[data-nt-currency-option]').forEach((option) => {
      const currency = option.dataset.ntCurrencyOption;
      const disabled = !rateIsAvailable(currency);
      option.disabled = disabled;
      option.setAttribute('aria-disabled', String(disabled));
    });
  };

  // Price filter inputs hold ZAR in data-zar-value and show it in the displayed currency.
  const refreshPriceFilters = () => {
    const rate = rates[displayedCurrency()];
    document.querySelectorAll('[data-nt-price-input]').forEach((input) => {
      const round = input.dataset.ntPriceInput === 'max' ? Math.ceil : Math.floor;
      const zar = input.dataset.zarValue;
      input.value = zar ? String(round(Number(zar) * rate)) : '';
      if (input.dataset.zarPlaceholder) input.placeholder = String(Math.ceil(Number(input.dataset.zarPlaceholder) * rate));
    });
  };

  const refreshMoney = () => {
    const currency = displayedCurrency();
    document.documentElement.dataset.ntCurrency = currency;
    document.querySelectorAll('[data-nt-money]').forEach((element) => {
      const prefix = element.dataset.prefix || '';
      const approximate = currency !== 'ZAR' && fxConfig.showApproximateSymbol !== false ? '≈ ' : '';
      element.textContent = `${prefix}${approximate}${formatMoney(element.dataset.zarCents, currency)}`;
      if (currency !== 'ZAR' && fxConfig.disclaimer) element.title = fxConfig.disclaimer;
      else element.removeAttribute('title');
    });
    document.querySelectorAll('[data-nt-currency-select]').forEach((select) => {
      select.value = currency;
    });
    document.querySelectorAll('[data-nt-active-currency]').forEach((element) => {
      element.textContent = currency;
    });
    document.querySelectorAll('[data-nt-currency-option]').forEach((option) => {
      const selected = option.dataset.ntCurrencyOption === currency;
      if (selected) option.setAttribute('aria-current', 'true');
      else option.removeAttribute('aria-current');
    });
    refreshPriceFilters();
    refreshRateStatus();
  };

  const setCurrency = (currency) => {
    if (!SUPPORTED.includes(currency) || !rateIsAvailable(currency)) return false;
    activeCurrency = currency;
    try {
      window.localStorage.setItem(STORAGE_KEY, currency);
    } catch (_error) {
      // Currency still works for the current page when storage is unavailable.
    }
    refreshMoney();
    document.dispatchEvent(new CustomEvent('natondi:currency-change', { detail: { currency } }));
    return true;
  };

  const normalizeRemoteRates = (payload) => {
    const arrayRates = Array.isArray(payload)
      ? Object.fromEntries(payload.map((item) => [item.quote, item.rate]))
      : null;
    const source = arrayRates || payload?.rates || payload?.conversion_rates || payload?.data?.rates;
    if (!source) return null;
    const eur = Number(source.EUR);
    const usd = Number(source.USD);
    if (!(eur > 0) || !(usd > 0)) return null;
    const sourceDate = Array.isArray(payload) ? payload.find((item) => item?.date)?.date : payload?.date;
    const parsedDate = sourceDate
      ? Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(sourceDate) ? `${sourceDate}T00:00:00Z` : sourceDate)
      : NaN;
    return {
      rates: { EUR: eur, USD: usd },
      rateTimestamp: Number.isFinite(parsedDate) ? parsedDate : null,
    };
  };

  const applyRates = (nextRates, timestamp = null) => {
    rates.EUR = nextRates.EUR;
    rates.USD = nextRates.USD;
    rateTimestamp = timestamp;
  };

  const readCachedRates = () => {
    try {
      const cached = JSON.parse(window.localStorage.getItem(RATE_CACHE_KEY));
      const normalized = cached ? normalizeRemoteRates({ rates: cached.rates }) : null;
      const fetchedAt = Number(cached?.fetchedAt || cached?.updatedAt);
      const cachedRateTimestamp = Number(cached?.rateTimestamp || fetchedAt);
      if (!normalized || !Number.isFinite(fetchedAt) || !Number.isFinite(cachedRateTimestamp)) return null;
      const fetchedAge = Date.now() - fetchedAt;
      const providerRateAge = Date.now() - cachedRateTimestamp;
      if (fetchedAge < 0 || fetchedAge > maxCacheAge || providerRateAge > maxProviderRateAge) return null;
      return {
        rates: normalized.rates,
        fetchedAt,
        rateTimestamp: cachedRateTimestamp,
        fetchedAge,
      };
    } catch (_error) {
      return null;
    }
  };

  const loadLiveRates = async () => {
    if (rateMode === 'manual') {
      const manual = normalizeRemoteRates({ rates: fxConfig.manualRates });
      const manualTimestamp = fxConfig.manualUpdatedAt ? Date.parse(`${fxConfig.manualUpdatedAt}T00:00:00Z`) : NaN;
      if (manual) {
        applyRates(manual.rates, Number.isFinite(manualTimestamp) ? manualTimestamp : null);
        rateState = 'manual';
      } else {
        rateState = 'unavailable';
      }
      refreshMoney();
      return;
    }

    const cached = readCachedRates();
    if (cached) {
      applyRates(cached.rates, cached.rateTimestamp);
      rateState = 'cached';
      refreshMoney();
      if (cached.fetchedAge >= 0 && cached.fetchedAge < RATE_REFRESH_AGE) return;
    }

    try {
      if (!fxConfig.rateEndpoint || !/^https:\/\//i.test(fxConfig.rateEndpoint)) throw new Error('A valid HTTPS rate endpoint is required.');
      const response = await fetch(fxConfig.rateEndpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const normalized = normalizeRemoteRates(await response.json());
      if (!normalized) throw new Error('The rate endpoint returned an unsupported response.');
      const fetchedAt = Date.now();
      const liveRateTimestamp = normalized.rateTimestamp || fetchedAt;
      if (fetchedAt - liveRateTimestamp > maxProviderRateAge) throw new Error('The rate endpoint returned rates older than the safety limit.');
      applyRates(normalized.rates, liveRateTimestamp);
      rateState = 'live';
      try {
        window.localStorage.setItem(RATE_CACHE_KEY, JSON.stringify({
          fetchedAt,
          rateTimestamp: liveRateTimestamp,
          rates: normalized.rates,
        }));
      } catch (_error) {
        // Live rates still apply for the current page when storage is unavailable.
      }
    } catch (error) {
      rateState = cached ? 'cached-fallback' : 'unavailable';
      console.warn('[Natondi] Live currency rates unavailable; foreign currency display is fail-safe.', error);
    }
    refreshMoney();
  };

  const bindCurrencyControls = () => {
    document.addEventListener('change', (event) => {
      const select = event.target.closest('[data-nt-currency-select]');
      if (select) setCurrency(select.value);
    });
    document.addEventListener('click', (event) => {
      const option = event.target.closest('[data-nt-currency-option]');
      if (!option) return;
      setCurrency(option.dataset.ntCurrencyOption);
      option.closest('details')?.removeAttribute('open');
    });
  };

  const bindDestinationSearch = () => {
    document.addEventListener('click', (event) => {
      const option = event.target.closest('[data-nt-destination-option]');
      if (!option) return;
      const form = option.closest('[data-nt-destination-form]');
      if (!form) return;
      form.dataset.ntDestinationUrl = option.dataset.url || '';
      const label = form.querySelector('[data-nt-destination-label]');
      if (label) label.textContent = option.dataset.label || option.textContent.trim();
      form.querySelectorAll('[data-nt-destination-option]').forEach((entry) => {
        if (entry === option) entry.setAttribute('aria-current', 'true');
        else entry.removeAttribute('aria-current');
      });
      const menu = option.closest('details');
      menu?.removeAttribute('open');
      menu?.querySelector('summary')?.removeAttribute('aria-invalid');
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-nt-destination-form]');
      if (!form) return;
      event.preventDefault();
      const select = form.querySelector('select');
      const target = form.dataset.ntDestinationUrl || select?.selectedOptions?.[0]?.dataset.url;
      if (target) {
        window.location.assign(target);
        return;
      }
      const menu = form.querySelector('.nt-destination-menu');
      if (menu) menu.setAttribute('open', '');
      const summary = menu?.querySelector('summary');
      summary?.setAttribute('aria-invalid', 'true');
      summary?.focus();
    });
  };

  const prefillTourInterest = () => {
    const params = new URLSearchParams(window.location.search);
    const tour = params.get('tour');
    if (!tour) return;
    const readableTour = params.get('tour_title') || tour.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
    const tourUrl = params.get('tour_url') || `${window.location.origin}/products/${encodeURIComponent(tour)}`;
    document.querySelectorAll('[data-nt-tour-interest]').forEach((input) => {
      if (!input.value) input.value = readableTour;
    });
    document.querySelectorAll('[data-nt-tour-url]').forEach((input) => {
      if (!input.value) input.value = new URL(tourUrl, window.location.origin).href;
    });
    document.querySelectorAll('[data-nt-tour-context]').forEach((context) => {
      const title = context.querySelector('[data-nt-tour-context-title]');
      if (title) title.textContent = readableTour;
      context.hidden = false;
    });
  };

  const bindEnquiryNewsletter = () => {
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.classList.contains('nt-contact-form--enquiry')) return;
      if (form.dataset.ntNewsletterProcessed === 'true') return;

      const optIn = form.querySelector('[data-nt-newsletter-opt-in]');
      if (!optIn?.checked) return;
      if (form.dataset.ntNewsletterProcessing === 'true') {
        event.preventDefault();
        return;
      }

      const newsletterForm = form.closest('.nt-contact-section')?.querySelector('.nt-enquiry-newsletter-form');
      const enquiryEmail = form.querySelector('input[name="contact[email]"]');
      const newsletterEmail = newsletterForm?.querySelector('[data-nt-newsletter-email]');
      if (!newsletterForm || !enquiryEmail?.value || !newsletterEmail) return;

      event.preventDefault();
      form.dataset.ntNewsletterProcessing = 'true';
      const submitter = event.submitter;
      if (submitter) submitter.disabled = true;
      newsletterEmail.value = enquiryEmail.value;

      try {
        await fetch(newsletterForm.action, {
          method: 'POST',
          body: new FormData(newsletterForm),
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
      } catch (error) {
        console.warn('[Natondi] Newsletter opt-in could not be completed.', error);
      } finally {
        form.dataset.ntNewsletterProcessed = 'true';
        delete form.dataset.ntNewsletterProcessing;
        if (submitter) submitter.disabled = false;
        if (submitter) form.requestSubmit(submitter);
        else form.requestSubmit();
      }
    });
  };

  const bindDrawers = () => {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-nt-drawer-trigger]');
      if (!trigger) return;
      const drawer = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!drawer) return;
      const open = drawer.hasAttribute('hidden');
      drawer.toggleAttribute('hidden', !open);
      trigger.setAttribute('aria-expanded', String(open));
      document.documentElement.classList.toggle('nt-drawer-open', open);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('.nt-drawer:not([hidden])').forEach((drawer) => drawer.setAttribute('hidden', ''));
      document.querySelectorAll('[data-nt-drawer-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      document.documentElement.classList.remove('nt-drawer-open');
    });
  };

  const bindNavigationMenus = () => {
    document.addEventListener('click', (event) => {
      const currentMenu = event.target.closest('details[data-nt-menu]');
      document.querySelectorAll('details[data-nt-menu][open]').forEach((menu) => {
        if (menu !== currentMenu) menu.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('details[data-nt-menu][open]').forEach((menu) => {
        menu.removeAttribute('open');
        menu.querySelector('summary')?.focus();
      });
    });
  };

  const bindCollectionFilters = () => {
    document.addEventListener('change', (event) => {
      const checkbox = event.target.closest('[data-nt-filter-form] input[type="checkbox"]');
      if (checkbox) checkbox.form.requestSubmit();
    });
    document.addEventListener('input', (event) => {
      const input = event.target.closest('[data-nt-price-input]');
      if (!input) return;
      input.dataset.zarValue = input.value === '' ? '' : String(Number(input.value) / rates[displayedCurrency()]);
    });
    // Shopify filters on the store currency, so typed amounts are converted back to ZAR before submitting.
    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-nt-filter-form]');
      if (!form) return;
      form.querySelectorAll('[data-nt-price-input]').forEach((input) => {
        if (!input.dataset.zarValue) {
          input.disabled = true;
          return;
        }
        const round = input.dataset.ntPriceInput === 'max' ? Math.ceil : Math.floor;
        input.value = String(round(Number(input.dataset.zarValue)));
      });
    });
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      document.querySelectorAll('[data-nt-price-input]').forEach((input) => {
        input.disabled = false;
      });
      refreshPriceFilters();
    });
  };

  let stickyHeaderScrollBound = false;
  let stickyHeaderFrame = 0;

  const getHeaderScrollTop = () => {
    const pageScroll = document.querySelector('.page-wrapper')?.scrollTop || 0;
    return Math.max(window.scrollY || 0, pageScroll);
  };

  const updateStickyHeaders = () => {
    stickyHeaderFrame = 0;
    document.querySelectorAll('[data-nt-sticky-header]').forEach((header) => {
      header.classList.toggle('nt-header--scrolled', getHeaderScrollTop() > 24);
    });
  };

  const queueStickyHeaderUpdate = () => {
    if (stickyHeaderFrame) return;
    stickyHeaderFrame = window.requestAnimationFrame(updateStickyHeaders);
  };

  const bindStickyHeaders = () => {
    updateStickyHeaders();
    if (stickyHeaderScrollBound) return;
    stickyHeaderScrollBound = true;
    window.addEventListener('scroll', queueStickyHeaderUpdate, { passive: true });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', queueStickyHeaderUpdate, { passive: true });
  };

  const revealSelector = '[data-nt-reveal]';
  let revealObserver;

  const elementsWithin = (scope, selector) => {
    const elements = scope.querySelectorAll ? Array.from(scope.querySelectorAll(selector)) : [];
    if (scope instanceof Element && scope.matches(selector)) elements.unshift(scope);
    return elements;
  };

  const prepareRevealGroups = (scope) => {
    elementsWithin(scope, '[data-nt-reveal-group]').forEach((group) => {
      Array.from(group.children)
        .filter((element) => element.matches(revealSelector))
        .forEach((element, index) => {
          element.style.setProperty('--nt-reveal-delay', `${Math.min(index, 5) * 70}ms`);
        });
    });
  };

  const bindReveal = (scope = document) => {
    prepareRevealGroups(scope);
    const elements = elementsWithin(scope, revealSelector);
    if (document.body.classList.contains('nt-motion--none') || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('nt-reveal--visible'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('nt-reveal--visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -32px', threshold: 0.08 });
    }
    elements.forEach((element) => {
      if (element.dataset.ntRevealBound === 'true') return;
      element.dataset.ntRevealBound = 'true';
      revealObserver.observe(element);
    });
  };

  window.NatondiCurrency = {
    get currency() { return activeCurrency; },
    setCurrency,
    setRates(nextRates) {
      const normalized = normalizeRemoteRates({ rates: nextRates });
      if (!normalized) return false;
      applyRates(normalized.rates, Date.now());
      rateState = 'manual';
      refreshMoney();
      return true;
    },
  };

  bindCurrencyControls();
  bindDestinationSearch();
  prefillTourInterest();
  bindEnquiryNewsletter();
  bindDrawers();
  bindNavigationMenus();
  bindCollectionFilters();
  bindStickyHeaders();
  refreshMoney();
  loadLiveRates();
  bindReveal();
  document.addEventListener('shopify:section:load', (event) => {
    refreshMoney();
    bindStickyHeaders();
    bindReveal(event.target);
  });
})();
